<!--
  The page to display the chatbot
-->

<template>
  <div class="chatbot-aside">
    <!-- dialog to create a new context -->
    <el-dialog width="500px"
      title="New context"
      :visible.sync="context.create.create">
      <el-form ref="formCreate" :model="context.create"
        label-width="120px"
        :hide-required-asterisk="true"
        @submit.native.prevent="newContext">
        <el-form-item label="Name"
          :required="true"
          prop="context"
          :rules="duplicateRules.concat(contextRules)">
          <context-editor
            v-model="context.create.context"
            :contexts="contextChoices"
            :intents="intentChoices"
            @rules="val => contextRules = val">
          </context-editor>
        </el-form-item>
        <el-form-item label="Type">
          <el-button-group>
            <el-select
              v-model="context.create.type">
              <el-option
                v-for="type in contextTypes"
                :key="type.value"
                :label="type.name"
                :value="type.value"></el-option>
            </el-select>
            <el-input-number style="flex: 0 1 100px"
              v-model="context.create.ttl"
              controls-position="right"
              :disabled="context.create.type !== 'temporary'"
              :min="1"></el-input-number>
          </el-button-group>
        </el-form-item>
        <div style="text-align: center;">
          <el-button native-type="submit">Create</el-button>
          <el-button @click="context.create.create = false">Cancel</el-button>
        </div>
      </el-form>
    </el-dialog>
    <!-- dialog to edit a context -->
    <el-dialog width="500px"
      :visible="!!context.context"
      @update:visible="uneditContext">
      <span slot="title" v-if="context.context">
        Context: {{contextName(context.context.context)}}
        <router-link v-bind="contextLink(context.context.context)"
          @click.native="linkContext"
          :tabindex="-1">
          <i class="el-icon-link"></i>
        </router-link>
      </span>
      <div
        v-for="variable in context.variables" :key="variable.name">
        <div class="context-variable-name" style="min-width: 120px; float: left">
          <el-button icon="el-icon-delete" circle size="mini"
            @click="deleteVariable(variable.name)"
            :tabindex="-1"></el-button>
            {{variable.name}}
        </div>
        <div class="context-variable-value"
          @dblclick="editVariable(variable)">{{variable.json}}</div>
      </div>
      <p style="text-align: center; clear: left;">
        <el-button @click="editVariable()">Add value</el-button>
        <el-button @click="deleteContext()">Delete</el-button>
        <el-button-group
          style="width: 195px; display: inline-block !important; margin-left: 10px">
          <el-select style="width: 120px"
            v-model="context.create.type">
            <el-option
              v-for="type in contextTypes"
              :key="type.value"
              :label="type.name"
              :value="type.value"></el-option>
          </el-select>
          <el-input-number style="width: 75px"
            v-model="context.create.ttl"
            controls-position="right"
            :disabled="context.create.type !== 'temporary'"
            :min="1"></el-input-number>
          </el-button-group>
      </p>
    </el-dialog>
    <!-- dialog to edit a variable in a context -->
    <el-dialog width="500px"
      :title="'Edit: ' + context.edit.name"
      :visible="!!context.edit.name && context.edit.name !== true"
      @update:visible="uneditVariable">
      <el-form ref="formEdit" :model="context.edit"
        label-width="120px"
        :hide-required-asterisk="true"
        @submit.native.prevent="updateVariable(context.edit.name, context.edit.json, $refs.formEdit)">
        <el-form-item label="Name">
          <el-input :value="context.edit.name" :disabled="true"></el-input>
        </el-form-item>
        <el-form-item prop="json" label="Value"
          :required="true" :rules="jsonRules">
          <el-input type="textarea" v-model="context.edit.json" :rows="5"></el-input>
        </el-form-item>
        <div style="text-align: center;">
          <el-button native-type="submit">Save</el-button>
          <el-button @click="context.edit.name = null">Cancel</el-button>
        </div>
      </el-form>
    </el-dialog>
    <!-- dialog to add a new variable in a context -->
    <el-dialog width="500px"
      :title="'Add value'"
      :visible="context.edit.name === true"
      @update:visible="uneditVariable">
      <el-form ref="formAdd" :model="context.edit"
        label-width="120px"
        :hide-required-asterisk="true"
        @submit.native.prevent="updateVariable(context.edit.newname, context.edit.json, $refs.formAdd)">
        <el-form-item prop="newname" label="Name"
          :required="true" :rules="nameRules">
          <el-input v-model="context.edit.newname"></el-input>
        </el-form-item>
        <el-form-item prop="json" label="Value"
          :required="true" :rules="jsonRules">
          <el-input type="textarea" v-model="context.edit.json" :rows="5"></el-input>
        </el-form-item>
        <el-button native-type="submit">Save</el-button>
        <el-button @click="context.edit.name = null">Cancel</el-button>
      </el-form>
    </el-dialog>
    <!-- the main content of the chatbot -->
    <div class="chatbot-slide" :class="{ 'is-active': active }">
      <div class="chatbot-page">
        <!-- A button to open and close the chatbot -->
        <div class="chatbot-head" @click="$emit('update:active', !active)">
          <h2>Test Chatbot
            <i class="el-icon-arrow-up" :class="{'is-active': active}"></i>
          </h2>
        </div>
        <!-- When submitted, this form sends a new user input -->
        <div class="chatbot-form">
          <h1>Chatbot: {{skillObj.data.name}}
            <div style="float: right; margin-left: 20px">
              <!--  a button to show or hide the contexts (small screen) -->
              <el-button icon="el-icon-setting" circle
                class="contexts-button"
                @click="openContext = !openContext"
                :tabindex="-1"></el-button>
              <!-- selector for the cache -->
              <el-select v-model="cache" style="width: 120px" :tabindex="-1">
                <el-option label="Test" value="test"></el-option>
                <el-option label="Published" value="published"></el-option>
                <el-option label="Approved" value="approved"></el-option>
              </el-select>
            </div>
          </h1>
          <!-- The list of all the contexts -->
          <p class="contexts-list"
            :class="{ 'contexts-list-opened': openContext }">
            <el-button style="padding: 6px; margin-right: 5px"
              v-for="ctxt in contexts" :key="ctxt.context"
              :type="context.context === ctxt ? (ctxt.exclusive ? 'danger' : 'primary') : (ctxt.exclusive ? 'warning' : null)"
              :disabled="ctxt.context === 'global'"
              @click="editContext(ctxt)"
              :tabindex="-1"
              >{{contextName(ctxt.context)}} ({{
                ctxt.ttl > 0 ? ctxt.ttl : '∞'
              }})</el-button>
            <el-button style="padding: 6px; margin-right: 5px"
              icon="el-icon-circle-plus-outline" circle
              @click="createContext"
              :tabindex="-1"></el-button>
          </p>
          <!-- The list of all messages -->
          <p class="chatbot-chat" v-chat-scroll>
            <el-alert effect="dark"
              v-for="(message, index) in messages" :key="index"
              :type="message.source === 'user' ? 'primary' : message.source === 'bot' ? 'success' : message.source === 'error' ? 'warning' : 'info'"
              :class="'chat-alert chat-alert-'+message.source"
              :closable="false">
              <span v-if="message.type === 'message'">{{message.text}}</span>
              <audio v-else-if="message.type === 'audio'"
                autoplay controls
                :src="message.url"
                @ended="message.ended"
                @error="message.ended"
                :tabindex="-1"></audio>
              <template v-else-if="message.type === 'uncached'">
                <span v-if="!message.object">
                  An intent could not be match due to a missing
                  <em>{{message.objecttype}}</em>.<br/>
                  Please contact an administrator.
                </span>
                <span v-else-if="message.object.local">
                  An intent could not be match because the
                  <em>{{message.objecttype}} </em>
                  <router-link :to="'/skills/' + skill + '/' + message.path">{{
                    message.object.name
                  }}</router-link> is not in cache.<br/>
                  Please cache it to avoid this error.
                </span>
                <span v-else>
                  An intent could not be match because the
                  <em>{{message.objecttype}} </em>
                  <strong>{{message.object.name}}</strong> is not in cache.<br/>
                  Please contact an administrator.
                </span>
              </template>
              <template v-else-if="message.source === 'login' && message.status === 'ok'">
                <p>
                  <i class="el-icon el-icon-success"></i>
                  Logged in to {{ message.name }}
                </p>
              </template>
              <template v-else-if="message.source === 'login' && message.status === 'error'">
                <p>
                  <i class="el-icon el-icon-error"></i>
                  Error: {{ message.error }}
                </p>
              </template>
              <el-form v-else-if="message.type === 'username-password'"
                :model="message.params"
                @submit.native.prevent="message.methods.submit"
                inline hide-required-asterisk>
                <p>Login to {{ message.name }}</p>
                <el-form-item label="username" prop="username" required>
                  <el-input v-model="message.params.username" type="text"
                    ></el-input>
                </el-form-item>
                <el-form-item label="password" prop="password" required>
                  <el-input v-model="message.params.password" type="text" class="password"
                    ></el-input>
                </el-form-item>
                <el-button-group>
                  <el-button type="danger" @click="message.methods.cancel">
                    Cancel
                  </el-button>
                  <el-button native-type="submit" type="success">
                    Login
                  </el-button>
                </el-button-group>
              </el-form>
              <template v-else-if="message.type === 'oauth2-authorization'">
                <p>Login to {{ message.name }}</p>
                <el-button-group>
                  <el-button type="danger" @click="message.methods.cancel">
                    Cancel
                  </el-button>
                  <el-button type="success"
                    @click="message.methods.submit">
                    Login
                  </el-button>
                </el-button-group>
              </template>
              <ul v-if="message.stack">
                <li v-for="(pos, index) in message.stack" :key="index">
                  line {{pos.line}} col {{pos.col}}
                </li>
              </ul>
            </el-alert>
          </p>
          <!-- The field for a new message -->
          <el-form class="chatbot-input"
            @submit.native.prevent="newInput"
            @click.native="$emit('update:active', true)">
            <el-button-group>
              <el-input :value="input" @input="handleInput"
                @keydown.up.native="handleUp" @keydown.down.native="handleDown"
                tabindex="-1"></el-input>
              <el-button native-type="submit" :disabled="!input || !!todo"
                :tabindex="-1">Send</el-button>
            </el-button-group>
          </el-form>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ChatBot from '../../utils/chatbot'
import { ParserError } from '../../utils/parser'
import { VariableValidator } from '../../utils/validators'
import State from '../../stores/state'
import ContextEditor from '../intent/ContextEditor.vue'

const DEFAULT_TTL = 3
const contextTypes = [{
  value: 'temporary',
  name: 'Temporary',
}, {
  value: 'unlimited',
  name: 'Unlimited',
}, {
  value: 'exclusive',
  name: 'Exclusive',
}]

export default {
  props: {
    skill: String,
    active: Boolean,
  },
  components: {
    ContextEditor,
  },
  data () {
    return {
      state: State.state, // for the user
      skillObj: State.empty, // the skill
      // a Chatbot object
      chatbot: new ChatBot(this.skill, 'test'),
      messages: [], // list of messages to display
      input: '', // the user input
      cache: 'test', // which cache to use
      history: null, // the position in the messages history
      todo: null, // the StreamingPromise from chatbot.js
      context: { // the context to view and edit
        context: null, // the context object, if editing
        variables: [], // the variables of the context as an array of objects
        edit: {}, // the variable being edited, if any
        create: { // the model for the context being created or edited
          create: false, // true to create, false to edit context.context
          type: contextTypes[0].value,
          ttl: DEFAULT_TTL,
        },
      },
      openContext: false, // if should display the list of context
      contextTypes, // the possible context types
      // the validator for context name
      // checks for unicity
      duplicateRules: [{
        validator: (rule, value, callback) => callback(
          this.contexts.find(x => x.context === value)
            ? new Error('duplicate context') : undefined),
        trigger: 'input',
      }],
      contextRules: [],
      // the validator for context value
      // interprets the value with a dangerous "eval" (implemented by function)
      jsonRules: {
        validator (rule, value, callback) {
          if (!value) callback(new Error('value required'))
          try {
            // eslint-disable-next-line no-new-func
            value = Function('"use strict";return(' + value + ')')()
          } catch (error) {
            return callback(error)
          }
          if (value === undefined ||
            typeof value === 'number' && !isFinite(value)) {
            return callback(new Error('invalid value ' + value))
          }
          return callback()
        },
      },
      // the rule for field name in context
      // checks for unicity
      nameRules: [VariableValidator, {
        validator: (rule, value, callback) => {
          callback(value in this.context.context.variables
            ? new Error('already used') : undefined)
        },
      }],
    }
  },
  // load the skill
  created () {
    this.loadSkill()
    window.chatbot = this.chatbot // for debugging
  },
  computed: {
    // the list of the contexts created by the chatbot
    contexts () { return this.chatbot.contexts[this.skill] || [] },
    // the known context, for context creation
    contextChoices () { return this.skillObj.data.contexts },
    // the available intents, for context creation
    intentChoices () {
      return this.skillObj.data.intents.map(intent => {
        return {
          value: intent.intent,
          name: intent.name,
          context: intent.context,
        }
      })
    },
    // the name of the intents, for displaying the followup contexts
    intentNames () {
      let out = {}
      for (let intent of this.skillObj.data.intents) {
        out[intent.intent] = intent.name
      }
      return out
    },
  },
  watch: {
    // reload skill on connection
    'state.user' () { this.loadSkill() },
    // reload the skill and the chatbot on skill change
    'skill' () {
      this.loadSkill()
      this.chatbot = new ChatBot(this.skill, this.cache)
      window.chatbot = this.chatbot // for debugging
    },
    // reaload the chatbot on cache change
    cache () {
      this.chatbot = new ChatBot(this.skill, this.cache)
      window.chatbot = this.chatbot // for debugging
    },
    // when editing a context type
    'context.create.type' (type) {
      let context = this.context.context
      if (!context) return
      if (type === 'exclusive' && !context.exclusive) {
        context.exclusive = true
        context.ttl = 1
      } else if (type === 'unlimited' &&
          (context.exclusive || context.ttl >= 0)) {
        context.exclusive = false
        context.ttl = -1
      } else if (type === 'temporary' &&
        (context.exclusive || context.ttl < 0)) {
        context.exclusive = false
        context.ttl = DEFAULT_TTL
      }
    },
    // when editing a context ttl
    'context.create.ttl' (ttl) {
      let context = this.context.context
      if (!context) return
      let type = this.context.create.type
      if (type !== 'temporary') return
      context.ttl = ttl
    },
  },
  methods: {
    // loads the skill data
    loadSkill () {
      let skill = this.skill
      State.getSkill(skill).then(data => {
        if (skill !== this.skill) return
        this.skillObj = data
      }).catch(this.onerror)
    },
    newInput () {
      /* Called when a new input is validated by the user
      Sends it to chatbot.js */
      this.history = null
      if (!this.input) return
      this.messages.push({
        source: 'user',
        type: 'message',
        text: this.input,
      })
      try {
        this.todo = this.chatbot.newInput(this.input)
        this.input = ''
        this.readMessage()
      } catch (error) {
        this.todo = null
        this.input = ''
        this.playError(error)
        throw error
      }
    },
    readMessage (next) {
      /* Reads the next message of todo list */
      this.todo.read().then(({ value, done }) => {
        if (done) {
          this.todo = null
          if (!next) this.playError(new Error('nothing to display'))
        } else this.playMessage(value)
      }, error => {
        console.error(error)
        this.todo = null
        this.playError(error)
      })
    },
    /* Displays an error */
    playError (error) {
      this.waiting = false
      let message = {
        source: 'error',
        type: 'message',
        text: error.message,
      }
      this.messages.push(message)
      if (error instanceof ParserError) {
        message.stack = error.codestack
      }
    },
    /* Displays an uncached message */
    playUncached (data) {
      this.waiting = false
      let obj = (this.skillObj.data[data.path.split('/')[0]] || []).find(
        x => x[data.objecttype] === data.objectid)
      let message = {
        source: 'error',
        type: 'uncached',
        objecttype: data.objecttype,
        objectid: data.objectid,
        path: data.path,
        object: obj,
      }
      this.messages.push(message)
    },
    /* Displays a message, with pretty "[...]" for simulating typing */
    playMessage (message) {
      let next = () => {
        this.readMessage(true)
      }
      if (message.type === 'nomatch') {
        this.messages.push({
          source: 'error',
          type: 'message',
          text: 'unrecognized',
        })
        return next()
      } else if (message.type === 'error') {
        this.playError(message)
        return next()
      } else if (message.type === 'uncached') {
        this.playUncached(message)
        return next()
      } else if (message.type === 'username-password') {
        let params = {
          username: '',
          password: '',
        }
        let pushedMessage = {
          source: 'login',
          type: 'username-password',
          status: 'new',
          name: message.name,
          params: params,
          error: '',
          methods: {
            submit: () => {
              message.resolve(params)
            },
            cancel: () => {
              message.reject(new Error('cancelled'))
            },
          },
        }
        message.promise.then(
          () => { pushedMessage.status = 'ok' },
          (error) => {
            pushedMessage.error = error.message
            pushedMessage.status = 'error'
          },
        )
        this.messages.push(pushedMessage)
      } else if (message.type === 'oauth2-authorization') {
        let pushedMessage
        pushedMessage = {
          source: 'login',
          type: 'oauth2-authorization',
          status: 'new',
          name: message.name,
          error: '',
          methods: {
            submit: () => {
              pushedMessage.status = 'clicked'
              window.open(message.authorization_url, 'login', 'popup')
            },
            cancel: () => {
              message.reject(new Error('cancelled'))
            },
          },
        }
        message.promise.then(
          () => { pushedMessage.status = 'ok' },
          (error) => {
            pushedMessage.error = error.message
            pushedMessage.status = 'error'
          },
        )
        this.messages.push(pushedMessage)
      }
      setTimeout(() => {
        message.source = 'bot'
        if (message.type === 'message') {
          let text = message.text
          message.text = '[...]'
          this.messages.push(message)
          setTimeout(() => {
            message.text = text
            next()
          }, 500 + 500 * Math.random())
        } else if (message.type === 'audio') {
          message.ended = next
          this.messages.push(message)
          setTimeout(next, 5000)
        } else next()
      }, 200)
    },
    handleInput (val) {
      /* Called when user input is modified, clears history */
      if (this.input === val) return
      this.history = null
      this.input = val
    },
    handleUp () {
      /* Displays the previous user input in the input field, on up key */
      if (this.history === null) this.history = this.messages.length
      for (let i = this.history - 1; i >= 0; i--) {
        let message = this.messages[i]
        if (message.source === 'user') {
          this.history = i
          this.input = message.text
          return
        }
      }
    },
    handleDown () {
      /* Displays the next user input in the input field, on down key */
      if (this.history === null) return
      for (let i = this.history + 1; i < this.messages.length; i++) {
        let message = this.messages[i]
        if (message.source === 'user') {
          this.history = i
          this.input = message.text
          return
        }
        this.history = null
        this.input = ''
      }
    },
    // creates an empty context, for context creation
    createContext () {
      this.context.create = {
        create: true,
        context: '',
        type: contextTypes[0].value,
        ttl: DEFAULT_TTL,
      }
    },
    // adds the new context to the chatbot
    newContext () {
      this.$refs.formCreate.validate(valid => {
        if (valid) {
          let context = { context: this.context.create.context }
          if (this.context.create.type === 'exclusive') {
            context.exclusive = true
            context.ttl = 1
          } else if (this.context.create.type === 'unlimited') {
            context.ttl = -1
          } else context.ttl = this.context.create.ttl
          if (!this.chatbot.contexts[this.skill]) {
            this.chatbot.contexts[this.skill] = []
          }
          this.context.create.create = false
          this.chatbot.addContext(this.skill, context)
        } else {
          this.$message.error('Please correct the errors and try again')
        }
      })
    },
    editContext (context) {
      /* Called when one context is clicked, displays it */
      if (this.context.context === context) this.context.context = null
      else {
        this.context.context = context
        this.context.create.type = context.exclusive ? 'exclusive'
          : context.ttl < 0 ? 'unlimited' : 'temporary'
        this.context.create.ttl = this.context.create.type === 'temporary'
          ? context.ttl : DEFAULT_TTL
        this.loadContext()
      }
    },
    loadContext () {
      /* Makes the contexts ready for user-friendly display */
      let variables = this.context.context.variables
      this.context.variables = []
      for (let name in variables) {
        this.context.variables.push({
          name: name,
          value: variables[name],
          json: JSON.stringify(variables[name]),
        })
      }
    },
    deleteVariable (name) {
      /* Deletes a field of the context */
      delete this.context.context.variables[name]
      this.loadContext()
    },
    // changes the variable value
    updateVariable (name, value, form) {
      if (form) {
        form.validate(valid => {
          if (valid) this.updateVariable(name, value)
          else {
            this.$message.error('Please correct the errors and try again')
          }
        })
        return
      }
      /* Updates a field of the context */
      this.context.context.variables[name] =
        // eslint-disable-next-line no-new-func
        Function('"use strict";return(' + value + ')')()
      this.context.edit = {}
      this.loadContext()
    },
    editVariable (variable) {
      /* Starts editing a field of the context */
      if (variable) {
        this.context.edit = {
          name: variable.name,
          json: variable.json,
        }
      } else {
        this.context.edit = {
          name: true,
          json: '',
          newname: '',
        }
      }
    },
    cancelEdit () {
      /* Cancels editing a field of the context */
      this.context.edit = {}
    },
    deleteContext () {
      /* Deletes a whole context */
      this.contexts.splice(this.contexts.indexOf(this.context.context), 1)
      this.context.context = null
    },
    // closes the context editor
    uneditContext (visible) {
      if (!visible) this.context.context = null
    },
    // closes the variable editor
    uneditVariable (visible) {
      if (!visible) this.context.edit.name = null
    },
    // the displayed name of a context
    contextName (context) {
      if (context.substr(0, 'followup:'.length) === 'followup:') {
        let intent = context.substr('followup:'.length)
        return '⮡' + (this.intentNames[intent] || '[Unknown intent]')
      } else {
        return context
      }
    },
    // the link to a context (hash in skill page)
    contextLink (context) {
      let hash
      if (context.substr(0, 'followup:'.length) === 'followup:') {
        hash = '#intents-' + context.substr('followup:'.length)
      } else {
        hash = '#context-' + context
      }
      return {
        to: {
          name: 'skill',
          params: { skill: this.skill },
          query: this.$route.query,
          hash: hash,
        },
        replace: this.$route.name === 'skill' &&
          this.$route.params.skill === this.skill,
      }
    },
    // closes the context editor and the chatbot on context link clicked
    linkContext () {
      this.context.context = null
      this.$emit('update:active', false)
    },
  },
}
</script>

<style lang="scss">
  @import "../../element-variables.scss";
  @import "../../variables.scss";

  .chatbot-aside {
    height: 100vh;
    flex: 550px 1 0;
    .chatbot-slide {
      z-index: $chatbot-z-index;
      -webkit-transition: all .3s linear 0s;
      transition: all .3s linear 0s;
      height: 100%;
    }
    .chatbot-page {
      height: 100%;
      display: flex;
      align-items: center;
      .chatbot-head {
        display: none;
        width: 50px;
        // background: #95A4B4;
        background: $--border-color-hover;
        height: 200px;
        cursor: pointer;
        color: #fff;
        border-radius: 15px 0 0 15px;
        box-shadow: -4px 0 8px -4px rgba(175, 177, 179, 0.8);
        h2 {
          font-size: 18px;
          transform: rotate(-90deg);
          white-space: nowrap;
          // width: 100%;
          // display: inline-block;
          margin-top: 139px;
          word-spacing: 5px;
          letter-spacing: 1.2px;
          i {
            font-weight: bold;
            transition: transform .3s, -webkit-transform .3s;
            &.is-active {
              transform: rotate(180deg);
            }
          }
        }
      }
      .chatbot-form {
        padding: 0 20px;
        height: 100ch;
        background-color: #F3F5F9;
        height: 100%;
        flex-grow: 1;

        display: flex;
        flex-direction: column;

        > * {
          flex-shrink: 0;
          flex-grow: 0;

          &.chatbot-chat {
            flex-shrink: 1;
            flex-grow: 1;
            overflow-y: auto;
          }

          &:last-child {
            margin-bottom: 20px;
          }
        }
      }
    }
    .el-dialog__header .el-icon-link {
      color: $--color-text-placeholder;
    }
  }

  @media (max-width: 1700px) {
    .chatbot-aside {
      flex: 0 0 0;
      .chatbot-slide {
        position: absolute;
        right: 15px;
        top: 0;
        width: 550px;
        max-width: 100vw;
        overflow-x: hidden;
        &:not(.is-active) {
          // display: none;
          width: 50px;
        }
      }
      .chatbot-page {
        width: 550px;
        max-width: 100vw;
        .chatbot-head {
          display: block;
        }
        .chatbot-form {
          box-shadow: -4px 0 8px -4px rgba(175, 177, 179, 0.8);
        }
      }
    }
  }

  .el-button.contexts-button {
    display: none;
  }

  @media (max-height: 400px) {
    .el-button.contexts-button {
      display: inline-block;
      margin-right: 10px;
    }
    .contexts-list {
      display: none;
      &.contexts-list-opened {
        display: block;
      }
    }
    .chatbot-form > * {
      margin-top: 10px !important;
      margin-bottom: 0 !important;
      &:last-child {
        margin-bottom: 10px !important;
      }
    }
  }

  .el-alert.chat-alert {
    width: auto;
    a {
      font-weight: bold;
      color: inherit;
    }
    .el-form-item__label {
      color: inherit;
    }
  }
  .el-alert.chat-alert + .el-alert.chat-alert {
    margin-top: 10px;
  }
  .el-alert.chat-alert-user {
    text-align: left;
    margin-right: 100px;
  }
  .el-alert.chat-alert-bot {
    text-align: right;
    margin-left: 100px;
  }
  .el-alert.chat-alert-error {
    text-align: center;
  }

  .context-variable-name {
    width: 120px;
    float: left;
    clear: left;
    line-height: 28px;
  }
  .context-variable-value {
    line-height: 28px;
    font-family: monospace;
  }

  .password input {
    text-security: disc;
    -webkit-text-security: disc;
  }
</style>
